import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { toDisplayAmount } from '../utils/currency';

const CheckoutFailed = () => {
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkPaymentStatus = async () => {
      const params = new URLSearchParams(location.search);
      const state = location.state || {};
      const paymentId = params.get('payment_id') || state.paymentId;
      const orderId = params.get('order_id') || state.orderId;
      const error = params.get('error') || state.error;

      if (error) {
        setErrorMessage(decodeURIComponent(error));
      }

      if (!paymentId && !orderId) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/xendit/payment-status?${paymentId ? 'paymentId=' + paymentId : 'orderId=' + orderId}`);
        const data = await response.json();
        
        if (data.success) {
          setPaymentDetails(data.payment);
        } else {
          console.error('Failed to fetch payment status:', data.error);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkPaymentStatus();
  }, [location.search, location.state]);

  const handleRetryPayment = () => {
    // Возврат к странице оплаты с сохраненными данными
    navigate('/checkout/payment');
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="max-w-md mx-auto mt-16 p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Проверяем статус платежа...</p>
        </div>
      </Layout>
    );
  }

  const getFailureReason = () => {
    if (errorMessage) {
      return errorMessage;
    }
    
    if (paymentDetails?.failure_reason) {
      const reasons = {
        'INSUFFICIENT_BALANCE': 'Недостаточно средств на счете',
        'CARD_DECLINED': 'Карта была отклонена банком',
        'EXPIRED_CARD': 'Срок действия карты истек',
        'INVALID_CVV': 'Неверный CVV код',
        'PROCESSOR_ERROR': 'Ошибка обработки платежа',
        'AUTHENTICATION_FAILED': 'Не удалось пройти аутентификацию',
        'CANCELLED': 'Платеж был отменен'
      };
      return reasons[paymentDetails.failure_reason] || paymentDetails.failure_reason;
    }
    
    return 'К сожалению, платеж не был завершен';
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto mt-16 p-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Платеж не выполнен
          </h1>
          <p className="text-gray-600 mb-6">
            {getFailureReason()}
          </p>
          
          {paymentDetails && (
            <div className="bg-gray-50 p-4 rounded-lg mb-6 text-left">
              <h3 className="font-semibold text-gray-900 mb-2">Детали платежа:</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">ID платежа:</span>
                  <span className="font-medium">{paymentDetails.id}</span>
                </div>
                {paymentDetails.reference_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">ID заказа:</span>
                    <span className="font-medium">{paymentDetails.reference_id}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Сумма:</span>
                  <span className="font-medium">
                    {new Intl.NumberFormat('ru-RU', {
                      style: 'currency',
                      currency: paymentDetails.currency || 'USD'
                    }).format(toDisplayAmount(paymentDetails.amount, paymentDetails.currency))}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Статус:</span>
                  <span className="font-medium text-red-600">
                    {paymentDetails.status === 'FAILED' ? 'Неудачно' : 
                     paymentDetails.status === 'CANCELLED' ? 'Отменено' : paymentDetails.status}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <button
              onClick={handleRetryPayment}
              className="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors"
            >
              Попробовать снова
            </button>
            <Link
              to="/checkout/payment"
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors inline-block"
            >
              Выбрать другой способ оплаты
            </Link>
            <Link
              to="/"
              className="w-full bg-gray-200 text-gray-900 py-3 px-6 rounded-lg font-medium hover:bg-gray-300 transition-colors inline-block"
            >
              На главную
            </Link>
          </div>

          <div className="mt-8 p-4 bg-yellow-50 rounded-lg">
            <h4 className="font-medium text-yellow-800 mb-2">Возможные решения:</h4>
            <ul className="text-sm text-yellow-700 text-left space-y-1">
              <li>• Проверьте данные карты и попробуйте снова</li>
              <li>• Убедитесь, что на карте достаточно средств</li>
              <li>• Попробуйте другой способ оплаты</li>
              <li>• Свяжитесь с вашим банком</li>
            </ul>
          </div>

          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              Нужна помощь? <Link to="/contact" className="text-blue-600 hover:underline">Свяжитесь с нами</Link>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CheckoutFailed;
